import { filterMedia } from "./mediaContext"
import { getAccessToken } from "../../services/authService"

// No automatic replay: one click produces one shot plan and one contact sheet.
export const runStoryboard = async (request, onEvent) => {
  const token = getAccessToken()
  if (!token) throw new Error("Please sign in again.")
  const response = await fetch(`${process.env.REACT_APP_DEV_API}/ai/storyboard`, {
    method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, 'X-AI-Request-Id': window.crypto.randomUUID() },
    body: JSON.stringify({ scriptId: request.scriptId, sceneId: request.sceneId, options: request.options, aspectRatio: request.aspectRatio, sourceStoryboardId: request.sourceStoryboardId, wardrobe: request.wardrobe || "", shots: request.shots }),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || `Storyboard could not start (${response.status}).`)
  }
  if (!response.body) throw new Error("Storyboard returned no response stream.")
  const reader = response.body.getReader(); const decoder = new TextDecoder()
  let buffer = ""; let complete = false; let error
  const consume = (line) => {
    if (!line.trim()) return
    const event = JSON.parse(line)
    if (event.type === "error") { error = event.message; return }
    if (error) return
    if (event.type === "complete") complete = true
    onEvent(event)
  }
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let end
      while ((end = buffer.indexOf("\n")) !== -1) { consume(buffer.slice(0, end)); buffer = buffer.slice(end + 1) }
    }
    buffer += decoder.decode(); if (buffer.trim()) consume(buffer)
  } finally { reader.releaseLock() }
  if (error) throw new Error(error)
  if (!complete) throw new Error("The storyboard did not finish. No automatic retry was made.")
}
// Server builds one PDF with all four frames and captions from the saved S3 images.
export const downloadStoryboardPdf = async (usageId) => {
  const token = getAccessToken()
  if (!token) throw new Error("Please sign in again.")
  const response = await fetch(`${process.env.REACT_APP_DEV_API}/ai/storyboard/${usageId}/download`, { headers: { Authorization: `Bearer ${token}` } })
  if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.message || "Download failed.") }
  const url = URL.createObjectURL(await response.blob())
  const a = document.createElement("a"); a.href = url; a.download = "storyboard.pdf"; a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
// Saved storyboards: read-only lookups of existing records — never regenerate, never charge credits.
const getJson = async (path) => {
  const token = getAccessToken()
  if (!token) throw new Error("Please sign in again.")
  const response = await fetch(`${process.env.REACT_APP_DEV_API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.message || "Could not load saved storyboards.")
  return data
}
export const listSavedStoryboards = (scriptId) => getJson(`/ai/storyboards${scriptId ? `?scriptId=${encodeURIComponent(scriptId)}` : ""}`).then((d) => filterMedia(d.storyboards || [], scriptId))
export const openSavedStoryboard = (id) => getJson(`/ai/storyboard/${id}`)
// Owner-only deletions: one frame's S3 image, or the whole saved storyboard. Record-keeping for Admin is untouched.
const sendDelete = async (path) => {
  const token = getAccessToken()
  if (!token) throw new Error("Please sign in again.")
  const response = await fetch(`${process.env.REACT_APP_DEV_API}${path}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.message || "Delete failed.")
  return data
}
export const deleteSavedStoryboardFrame = (id, frame) => sendDelete(`/ai/storyboard/${id}/frame/${frame}`)
export const deleteSavedStoryboard = (id) => sendDelete(`/ai/storyboard/${id}`)

// View Only access: the owner's saved storyboard images for one shared screenplay (no download/edit/AI).
export const listSharedStoryboards = (scriptId) => getJson(`/ai/shared-storyboards?scriptId=${encodeURIComponent(scriptId)}`)
